<template>
  <div class="social-post-rating" ref="containerRef">
    <!-- Unrated state -->
    <div v-if="!hasRating" class="social-post-rating__trigger" @click="isOpen = true">
      <IconEmbedded
        :class="{
          'social-post-rating__trigger-star': true,
          'social-post-rating__trigger-star--yellow': !props.isApproved,
        }"
        name="star-solid"
        :size="22"
      />
      <IconEmbedded v-if="!props.isApproved" class="social-post-rating__trigger-thumbs" name="dislike_2" :size="20" />
    </div>

    <!-- Rated state -->
    <div v-else class="social-post-rating__trigger" @click="isOpen = true">
      <div class="social-post-rating__partial-star">
        <IconEmbedded name="check_2-5" :size="19" />
        <IconEmbedded v-if="!props.isApproved" class="social-post-rating__trigger-thumbs" name="dislike_2" :size="19" />
      </div>
    </div>
    <!-- Rated state (old)
    <div v-else class="social-post-rating__rated" @click="isOpen = true">
      <div class="social-post-rating__partial-star">
        <!- Background (unfilled) star 
        <IconEmbedded class="social-post-rating__star-bg" name="star-solid" :size="20" />
        <!- Foreground (filled) star with clip 
        <div class="social-post-rating__star-fill" :style="{ width: fillPercentage + '%' }">
          <IconEmbedded class="social-post-rating__star-fg" name="star-solid" :size="20" />
        </div>
      </div>
    </div>-->

    <!-- Rating UI (overlay) -->
    <Transition name="fade">
      <div v-if="isOpen" class="social-post-rating__overlay">
        <span class="social-post-rating__label">How'd we do?</span>
        <!-- Star rating -->
        <ul class="social-post-rating__stars" @mouseleave="resetHoveredRating">
          <li class="social-post-rating__star" v-for="index in 4" :key="index" @mouseover="hoveredStarIndex = index - 1" @click="handleRating(index)">
            <div
              :class="{
                'social-post-rating__star-icon-wrap': true,
                'social-post-rating__star-icon-wrap--filled': index - 1 <= hoveredStarIndex,
              }"
            >
              <IconEmbedded class="social-post-rating__star-icon" name="star-solid" :size="22" />
            </div>
          </li>
        </ul>

        <!-- Action buttons -->
        <div v-if="!props.isApproved" class="social-post-rating__buttons">
          <SrpButton
            class="social-post-rating__button"
            :class="{ 'social-post-rating__button--secondary': !isRemoveHovered && (hoveredStarIndex >= 2 || hoveredStarIndex < 0) }"
            size="tiny"
            :color="isRemoveHovered || (hoveredStarIndex >= 0 && hoveredStarIndex < 2) ? 'red' : 'gray'"
            fill="outlined"
            @mouseenter="isRemoveHovered = true"
            @mouseleave="isRemoveHovered = false"
            @click="handleRemove"
          >
            <template #icon><IconEmbedded name="dislike_2" :size="16" /></template>
            Remove
          </SrpButton>

          <SrpButton
            class="social-post-rating__button"
            :class="{ 'social-post-rating__button--secondary': !isApproveHovered && hoveredStarIndex < 2 }"
            size="tiny"
            :color="isApproveHovered || hoveredStarIndex >= 2 ? undefined : 'gray'"
            :fill="isApproveHovered || hoveredStarIndex >= 2 ? 'solid' : 'outlined'"
            @mouseenter="isApproveHovered = true"
            @mouseleave="isApproveHovered = false"
            @click="handleApprove"
          >
            <template #icon><IconEmbedded name="check_3-5" :size="16" /></template>
            Approve
          </SrpButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    rating?: number;
    isApproved?: boolean;
  }>(),
  {
    rating: 0,
    isApproved: false,
  }
);

const emit = defineEmits<{
  (e: "rate", rating: number): void;
  (e: "approve"): void;
  (e: "remove"): void;
}>();

// Refs
const containerRef = ref<HTMLElement | null>(null);

// State
const isOpen = ref<boolean>(false);
const hoveredStarIndex = ref<number>(-1);
const localRating = ref<number>(0);
const isRemoveHovered = ref<boolean>(false);
const isApproveHovered = ref<boolean>(false);
const hasRating = computed<boolean>(() => localRating.value > 0);

onMounted(() => {
  localRating.value = props.rating;
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

watch(
  () => props.rating,
  newRating => {
    localRating.value = newRating;
  }
);

// When overlay opens, initialize hoveredStarIndex to show existing rating
watch(isOpen, opened => {
  if (opened) {
    resetHoveredRating();
  }
});

function handleClickOutside(event: MouseEvent) {
  if (!containerRef.value) return;
  if (!containerRef.value.contains(event.target as Node)) {
    isOpen.value = false;
    // Reset to saved rating when closing without saving
    localRating.value = props.rating;
    hoveredStarIndex.value = -1;
  }
}

function handleRating(starCount: number) {
  localRating.value = starCount;
  emit("rate", starCount);
}

function handleRemove() {
  emit("remove");
  isOpen.value = false;
}

function handleApprove() {
  emit("approve");
  isOpen.value = false;
}

function resetHoveredRating() {
  hoveredStarIndex.value = localRating.value > 0 ? localRating.value - 1 : -1;
}
</script>

<style scoped lang="scss">
.social-post-rating {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: 30;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 2px;
    cursor: pointer;
    user-select: none;
    border-radius: 12px;
    padding: 4px 6px; // Pushes the animation away from the edge of the button
    margin: -4px -6px; // Offset the padding above
    animation: pulse-glow 3s ease-in-out 3;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.08);
    }
  }

  &__trigger-star {
    :deep(svg) {
      fill: none;
      stroke: lightgray;
      stroke-width: 2px;
      stroke-linejoin: round;
    }

    &--yellow {
      :deep(svg) {
        stroke: rgba(255, 193, 7, 1);
      }
    }
  }

  &__trigger-thumbs {
    :deep(svg) {
      fill: rgba(158, 158, 158, 1);
    }
  }

  &__rated {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }

  &__partial-star {
    position: relative;
    display: inline-flex;
  }

  &__star-bg {
    :deep(svg) {
      fill: rgba(204, 204, 204, 1);
    }
  }

  &__star-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    overflow: hidden;
  }

  &__star-fg {
    :deep(svg) {
      fill: rgba(255, 220, 35, 1);
      stroke: rgba(208, 186, 105, 1);
      stroke-width: 1px;
      stroke-linejoin: round;
    }
  }

  &__overlay {
    position: absolute;
    top: -8px;
    right: 0;
    padding: 10px 12px;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    gap: 7px;
    z-index: 25;
  }

  &__label {
    margin-bottom: -4px;
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-align: center;
  }

  &__stars {
    display: flex;
    justify-content: center;
    gap: 4px;
    padding: 0;
    margin: 0 20px;
    list-style: none;
  }

  &__star {
    cursor: pointer;
    user-select: none;
  }

  &__star-icon-wrap {
    pointer-events: none;

    :deep(svg) {
      fill: rgba(204, 204, 204, 1);
      transition: all 0.15s ease;
    }

    &--filled {
      :deep(svg) {
        fill: rgba(255, 220, 35, 1);
        stroke: rgba(208, 186, 105, 1);
        stroke-width: 1.3px;
        stroke-linejoin: round;
      }
    }
  }

  &__star-icon {
    pointer-events: none;
  }

  &__buttons {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  &__button {
    // Override the button sizes since the smallest isn't small enough
    transition: all 0.2s ease;
    height: 30px !important;
    min-height: 30px !important;
    padding: 0 14px 2px !important;
    font-size: 13px !important;

    :deep(.srp-button__icon-wrap) {
      min-width: 15px;
      min-height: 15px;
      margin-right: 5px;
    }

    &--secondary {
      opacity: 0.6;
    }
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Pulse glow animation for unrated trigger
@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
  }
  50% {
    box-shadow: 0 0 8px 2px rgba(255, 193, 7, 0.4);
  }
}
</style>
